import React, { useMemo, useState } from 'react';
import { useHGS } from '../context/HGSContext';
import { MODULE_SCHEMAS } from '../data/masterData';
import { diffMasterRows } from '../data/schemaDiff';
import { X, ArrowRight, Plus, Minus, FilePenLine, Columns3, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';

/**
 * Side-by-side Schema Diff Viewer.
 *
 *   props:
 *     moduleKey  — which master to compare (risk/pcc/fraud/dlc)
 *     onClose    — modal close handler
 *
 * UX:
 *   Top: base ▾  →  compare ▾   pickers (drawn from version snapshots)
 *   KPI strip: rows added / removed / modified / unchanged
 *   Column diff: chips for added / removed / stable
 *   Row diff: tabbed list (Added / Modified / Removed) with per-row expand-to-see field-level before/after
 */
export default function SchemaDiffViewer({ moduleKey, onClose }) {
  const { versionSnapshots, versionHistory } = useHGS();
  const snapshots = versionSnapshots[moduleKey] || {};
  const schema = MODULE_SCHEMAS[moduleKey];
  const idField = schema?.idField;

  // Versions list, newest first (uses versionHistory ordering when available,
  // falls back to keys of `snapshots`).
  const versions = useMemo(() => {
    const hist = (versionHistory[moduleKey] || []).map((h) => h.version);
    const all = [...new Set([...hist, ...Object.keys(snapshots)])];
    return all.filter((v) => !!snapshots[v]);
  }, [versionHistory, snapshots, moduleKey]);

  const [baseVer, setBaseVer] = useState(() => versions[1] || versions[0] || '');
  const [compareVer, setCompareVer] = useState(() => versions[0] || '');
  const [tab, setTab] = useState('modified');
  const [expanded, setExpanded] = useState({});

  const diff = useMemo(() => {
    if (!baseVer || !compareVer || !idField) return null;
    const base = (snapshots[baseVer]?.rows) || [];
    const comp = (snapshots[compareVer]?.rows) || [];
    return diffMasterRows(idField, base, comp);
  }, [snapshots, baseVer, compareVer, idField]);

  const canDiff = versions.length >= 2;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid={`schema-diff-${moduleKey}`}>
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden border border-slate-200">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-slate-50 to-white">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{schema?.label || moduleKey}</div>
            <div className="text-lg font-bold text-slate-900">Schema Diff Viewer</div>
          </div>
          <div className="flex items-center gap-3">
            <VersionPicker
              label="Base"
              value={baseVer}
              versions={versions}
              onChange={setBaseVer}
              snapshots={snapshots}
              testid="diff-base-version"
            />
            <ArrowRight className="w-4 h-4 text-slate-400" />
            <VersionPicker
              label="Compare"
              value={compareVer}
              versions={versions}
              onChange={setCompareVer}
              snapshots={snapshots}
              testid="diff-compare-version"
            />
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-md transition" data-testid="diff-close">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {!canDiff && (
            <div className="text-center py-16 text-slate-500" data-testid="diff-empty">
              <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-amber-500" />
              <div className="text-sm font-semibold">Only {versions.length} version snapshot available.</div>
              <div className="text-xs mt-1">Publish another version of this master to enable diff comparison.</div>
            </div>
          )}

          {canDiff && baseVer === compareVer && (
            <div className="text-center py-16 text-slate-500" data-testid="diff-same-version">
              <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-amber-500" />
              <div className="text-sm font-semibold">Base and Compare are the same version.</div>
              <div className="text-xs mt-1">Pick a different version on either side to see changes.</div>
            </div>
          )}

          {canDiff && baseVer !== compareVer && diff && (<>
            {/* KPI strip */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
              <Kpi label="Base rows"     value={diff.counts.base}     tone="slate" />
              <Kpi label="Compare rows"  value={diff.counts.compare}  tone="slate" />
              <Kpi label="Added"         value={diff.counts.added}    tone="emerald" testid="diff-kpi-added" />
              <Kpi label="Removed"       value={diff.counts.removed}  tone="red" testid="diff-kpi-removed" />
              <Kpi label="Modified"      value={diff.counts.modified} tone="amber" testid="diff-kpi-modified" />
            </div>

            {/* Column diff */}
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 mb-4">
              <div className="flex items-center gap-2 mb-2">
                <Columns3 className="w-4 h-4 text-slate-600" />
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-700">Column-Level Changes</span>
              </div>
              <div className="flex flex-wrap gap-1.5" data-testid="diff-columns">
                {diff.columns.added.map((c) => (
                  <span key={c} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-50 border border-emerald-200 text-[10px] font-mono text-emerald-800">
                    <Plus className="w-2.5 h-2.5" />{c}
                  </span>
                ))}
                {diff.columns.removed.map((c) => (
                  <span key={c} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-red-50 border border-red-200 text-[10px] font-mono text-red-800 line-through">
                    <Minus className="w-2.5 h-2.5" />{c}
                  </span>
                ))}
                {diff.columns.stable.slice(0, 12).map((c) => (
                  <span key={c} className="px-2 py-0.5 rounded-md bg-white border border-slate-200 text-[10px] font-mono text-slate-500">{c}</span>
                ))}
                {diff.columns.stable.length > 12 && (
                  <span className="px-2 py-0.5 text-[10px] text-slate-400">+{diff.columns.stable.length - 12} stable</span>
                )}
                {diff.columns.added.length + diff.columns.removed.length === 0 && (
                  <span className="text-[11px] italic text-slate-500">No column changes — only data changed between these versions.</span>
                )}
              </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-slate-200 mb-3 flex items-center gap-1">
              <DiffTab id="modified" current={tab} setCurrent={setTab} label="Modified" count={diff.counts.modified} tone="amber" />
              <DiffTab id="added"    current={tab} setCurrent={setTab} label="Added"    count={diff.counts.added}    tone="emerald" />
              <DiffTab id="removed"  current={tab} setCurrent={setTab} label="Removed"  count={diff.counts.removed}  tone="red" />
            </div>

            {/* Row list */}
            <div data-testid="diff-row-list">
              {tab === 'modified' && (diff.modified.length === 0
                ? <Empty msg="No modified rows between these versions." />
                : diff.modified.map((m) => (
                    <ModifiedRow key={m.id} m={m} expanded={!!expanded[m.id]} onToggle={() => setExpanded((e) => ({ ...e, [m.id]: !e[m.id] }))} questionKey={questionKeyFor(moduleKey)} />
                  )))}
              {tab === 'added' && (diff.added.length === 0
                ? <Empty msg="No new rows in the compare version." />
                : diff.added.map((r) => <SimpleRow key={r[idField]} row={r} idField={idField} tone="emerald" icon={Plus} questionKey={questionKeyFor(moduleKey)} />))}
              {tab === 'removed' && (diff.removed.length === 0
                ? <Empty msg="No removed rows between these versions." />
                : diff.removed.map((r) => <SimpleRow key={r[idField]} row={r} idField={idField} tone="red" icon={Minus} questionKey={questionKeyFor(moduleKey)} />))}
            </div>
          </>)}
        </div>
      </div>
    </div>
  );
}

// ─── helpers ──────────────────────────────────────────────────────────────
function questionKeyFor(moduleKey) {
  return {
    risk:  'description',
    pcc:   'complianceRequirement',
    fraud: 'controlQuestion',
    dlc:   'declarationQuestion',
  }[moduleKey] || null;
}

function VersionPicker({ label, value, versions, onChange, snapshots, testid }) {
  return (
    <label className="inline-flex flex-col gap-0.5 text-[9px] font-bold uppercase tracking-wider text-slate-500">
      {label}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="px-2 py-1 bg-white border border-slate-300 rounded-md text-xs font-mono text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#CC0000]/30"
        data-testid={testid}
      >
        {versions.length === 0 && <option value="">No snapshots</option>}
        {versions.map((v) => (
          <option key={v} value={v}>{v} ({snapshots[v]?.rows?.length ?? 0} rows)</option>
        ))}
      </select>
    </label>
  );
}

function Kpi({ label, value, tone, testid }) {
  const toneMap = {
    slate:   'bg-slate-50 text-slate-800 border-slate-200',
    emerald: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    red:     'bg-red-50 text-red-800 border-red-200',
    amber:   'bg-amber-50 text-amber-800 border-amber-200',
  };
  return (
    <div className={`rounded-lg border px-3 py-2 ${toneMap[tone] || toneMap.slate}`} data-testid={testid}>
      <div className="text-[9px] font-bold uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-xl font-bold tabular-nums">{value}</div>
    </div>
  );
}

function DiffTab({ id, current, setCurrent, label, count, tone }) {
  const active = current === id;
  const toneMap = {
    emerald: 'border-emerald-600 text-emerald-700',
    red:     'border-red-600 text-red-700',
    amber:   'border-amber-600 text-amber-800',
  };
  return (
    <button
      onClick={() => setCurrent(id)}
      className={`px-3 py-2 text-xs font-semibold border-b-2 transition ${active ? toneMap[tone] : 'border-transparent text-slate-500 hover:text-slate-800'}`}
      data-testid={`diff-tab-${id}`}
    >
      {label}
      <span className="ml-1.5 px-1.5 py-0.5 rounded bg-slate-100 text-[10px] font-mono text-slate-700">{count}</span>
    </button>
  );
}

function Empty({ msg }) {
  return <div className="text-center py-10 text-[11px] italic text-slate-500" data-testid="diff-tab-empty">{msg}</div>;
}

function SimpleRow({ row, idField, tone, icon: Icon, questionKey }) {
  const toneMap = {
    emerald: 'border-emerald-200 bg-emerald-50/40 text-emerald-900',
    red:     'border-red-200 bg-red-50/40 text-red-900',
  };
  return (
    <div className={`rounded-lg border ${toneMap[tone]} px-3 py-2 mb-2 flex items-start gap-2`}>
      <Icon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="font-mono text-[11px] font-bold">{row[idField]}</div>
        {questionKey && row[questionKey] && (
          <div className="text-[11px] leading-snug text-slate-700 mt-0.5">{row[questionKey]}</div>
        )}
      </div>
    </div>
  );
}

function ModifiedRow({ m, expanded, onToggle, questionKey }) {
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/30 mb-2">
      <button onClick={onToggle} className="w-full text-left px-3 py-2 flex items-start gap-2" data-testid={`diff-row-${m.id}`}>
        {expanded ? <ChevronDown className="w-3.5 h-3.5 mt-0.5 text-amber-700" /> : <ChevronRight className="w-3.5 h-3.5 mt-0.5 text-amber-700" />}
        <FilePenLine className="w-3.5 h-3.5 mt-0.5 text-amber-700 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[11px] font-bold text-amber-900">{m.id}</div>
          {questionKey && m.compare[questionKey] && (
            <div className="text-[11px] leading-snug text-slate-700 mt-0.5">{m.compare[questionKey]}</div>
          )}
          <div className="text-[10px] mt-1 inline-flex gap-1.5">
            {m.changes.slice(0, 4).map((c) => (
              <span key={c.field} className="px-1.5 py-0.5 rounded bg-amber-100 border border-amber-200 font-mono text-amber-900">{c.field}</span>
            ))}
            {m.changes.length > 4 && <span className="text-amber-700 italic">+{m.changes.length - 4} more</span>}
          </div>
        </div>
      </button>
      {expanded && (
        <div className="border-t border-amber-200 px-3 py-2 grid grid-cols-1 md:grid-cols-2 gap-2 bg-white">
          {m.changes.map((c) => (
            <div key={c.field} className="rounded-md border border-slate-200 p-2 text-[11px]">
              <div className="font-bold font-mono text-slate-700 mb-1">{c.field}</div>
              <div className="text-red-700 line-through opacity-80 break-words">{renderVal(c.before)}</div>
              <div className="text-emerald-700 mt-0.5 break-words">{renderVal(c.after)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function renderVal(v) {
  if (v == null || v === '') return <span className="italic text-slate-400">— empty —</span>;
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}
